/**
 * @fileoverview Atom: the small outline icon set the app draws on.
 * @module components/atoms/Icon
 */

import "./Icon.css"

/** Every icon the app can render. */
export type IconName =
	"chevron-down" | "close" | "plus" | "minus" | "pencil" | "trash" | "check"

/** Props accepted by {@link Icon}. */
export interface IconProps {
	/** Which glyph to draw. */
	readonly name: IconName
	/** Edge length in pixels. Defaults to 20. */
	readonly size?: number
}

/** Stroke path for each glyph, drawn on a 24×24 grid. */
const PATHS: Record<IconName, string> = {
	"chevron-down": "M5 8.5 12 15.5 19 8.5",
	close: "M6 6 18 18M18 6 6 18",
	plus: "M12 5v14M5 12h14",
	minus: "M5 12h14",
	pencil: "M15.5 4.5 19.5 8.5 8 20H4v-4ZM13.5 6.5 17.5 10.5",
	trash: "M4 7h16M9 7V5a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2M6 7l1 13a1 1 0 0 0 1 1h8a1 1 0 0 0 1-1l1-13",
	check: "M5 13 9.5 17.5 19 7",
}

/**
 * Outline icon, decorative by default.
 *
 * @param props - Which glyph to draw and its size.
 * @returns The rendered SVG. Pair it with a labelled control — it carries no
 *   accessible name of its own.
 *
 * @example
 * <Button ariaLabel="Supprimer"><Icon name="trash" /></Button>
 */
export function Icon({ name, size = 20 }: IconProps) {
	return (
		<svg
			className="icon"
			width={size}
			height={size}
			viewBox="0 0 24 24"
			fill="none"
			stroke="currentColor"
			strokeWidth={2}
			strokeLinecap="round"
			strokeLinejoin="round"
			aria-hidden="true"
		>
			<path d={PATHS[name]} />
		</svg>
	)
}
